/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck — Clerk core 3 migration (PR #1298, commit 89d531a53) made
// SetActive incompatible with SetActiveForOrg. Re-enable once the migration
// follow-up rewires this to the new typed shape.
/**
 * ClientSwitcher — merged Org/Brand switcher (one icon, one popover).
 *
 * Trigger: a single button showing only the active brand avatar (the org is
 * implied — the user already knows which org they're in; it surfaces in the
 * tooltip as "Brand · Org"). One combined popover presents both selections as
 * a tree: the active org pinned at top with its brands nested underneath, then
 * the other orgs as switch rows, then inline create actions.
 *
 *   - Org rows: data + switch via `useOrgSwitcher` (shared auth-context org
 *     list + `switchSelfOrg`). Selecting another org hard-navigates to
 *     `/o/{slug}` (which redirects to the alphabetically first brand for the
 *     new org, or `/b/new`). Selecting the already-active org soft-navigates
 *     to the org workspace overview (`/o/{slug}/overview`) — same scope, no
 *     reload. Only the active org's brands are loaded, so other orgs render
 *     as bare rows (their brands appear after the switch lands).
 *   - Brand rows: data fetched through `useBrandWorkspaces`; click keeps the
 *     current page under the new brand slug (`buildBrandSwitchUrl`; brand-owned
 *     resource detail pages fall back to their list page). The brand layout's
 *     CurrentBrandProvider becomes the new source of truth on landing — there
 *     is no client state to sync.
 *   - Create brand: own dialog → POST /api/brands with slug=slugify(name);
 *     409 → retry once with `details.suggested`; still conflict → toast.
 *     Landing: `/b/{newSlug}/chat/new` (URL-driven; brand layout's
 *     CurrentBrandProvider drives the chrome).
 *   - Create org: routes to `/onboarding/new-org` page (org name → brand
 *     in one chain). Full page (not dialog) so the user is on a brand-
 *     agnostic URL during the createOrganization → setActive switch —
 *     a dialog kept the user on /b/{prev-slug}/chat/new, which 404'd
 *     mid-flight under the new org's auth context.
 *   - Manage members: lives on the dedicated /settings/members page
 *     (custom MembersPanel, no Clerk hosted UI).
 */
'use client'

import * as React from 'react'
import { usePathname, useRouter } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import {
  Building2Icon,
  CheckIcon,
  ChevronRightIcon,
  ChevronsUpDownIcon,
  Loader2Icon,
  PlusIcon,
} from 'lucide-react'

import { toast } from 'sonner'
import { useAuthContext } from '@/hooks/use-auth-context'
import { ApiFetchError, useApiRequest } from '@/hooks/use-api'

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import { SidebarMenu, SidebarMenuItem } from '@/components/ui/sidebar'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { BrandAvatar } from '@/components/brand-avatar'
import { useOptionalCurrentBrand } from '@/hooks/use-current-brand'
import { useUnreadCountByBrand } from '@/hooks/use-notification-poll'
import { useOptionalActiveOrg } from '@/providers/active-scope-provider'
import { completeQuickAddBrandProfile } from '@/lib/brand-onboarding-actions'
import { slugifyBrandName as slugify } from '@/lib/brand-slug'
import {
  type BrandWorkspace,
  useBrandWorkspaces,
} from '@/hooks/use-brand-workspaces'
import { useOrgAdminGate } from '@/hooks/use-org-access'
import { useOrgSwitcher } from '@/hooks/use-org-switcher'
import { isOrgWorkspacePath } from '@/lib/rail-hrefs'
import { buildBrandSwitchUrl, buildBrandUrl, buildOrgUrl } from '@/lib/routes'
import { cn } from '@/lib/utils'
import { track } from '@/lib/analytics/track'
import { useBillingDialogStore } from '@/stores/billing-dialog-store'
import { useProductBrand } from '@/providers/product-brand-provider'

const EMPTY_BRANDS: BrandWorkspace[] = []

/**
 * cmdk value for the org workspace row. Carries both locales' keywords so
 * typing "workspace" / "工作区" finds it, and is shared with the Command's
 * `defaultValue` — the initial cursor can only land on this row if the two
 * strings match exactly, so they must come from one place.
 */
const ORG_WORKSPACE_ITEM_VALUE =
  '__org-workspace organization workspace 组织工作区'

type CreateBrandPayload = {
  id: string
  slug: string
}

type CreateBrandAttempt =
  | { ok: true; brand: CreateBrandPayload }
  | { ok: false; status: number; suggestedSlug?: string; errorCode?: string }

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)
    : null
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined
}

function extractSuggestedBrandSlug(details: unknown): string | undefined {
  const root = asRecord(details)
  if (!root) return undefined

  const rootDetails = asRecord(root.details)
  const error = asRecord(root.error)
  const errorDetails = asRecord(error?.details)

  return (
    asString(root.suggested) ??
    asString(rootDetails?.suggested) ??
    asString(error?.suggested) ??
    asString(errorDetails?.suggested)
  )
}

function extractErrorCode(details: unknown): string | undefined {
  const root = asRecord(details)
  if (!root) return undefined
  const error = asRecord(root.error)
  const detail = asRecord(root.detail)
  const detailError = asRecord(detail?.error)
  return asString(error?.code) ?? asString(detailError?.code)
}

// toolfare calls a brand a workspace: it is what an approved AI app reads and
// spends from, and toolfare has no brand profile, campaigns or brand context.
// Same keys, so every call below reads either namespace.
function useSwitcherTranslations() {
  const { product } = useProductBrand()
  return useTranslations(
    product === 'toolfare' ? 'Common.workspaceSwitcher' : 'Common.brandSwitcher',
  )
}

// ---------------------------------------------------------------------------
// Create brand dialog
// ---------------------------------------------------------------------------

function CreateBrandDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: (slug: string) => void
}) {
  const t = useSwitcherTranslations()
  // The website only seeds Soku's brand profile; toolfare has none to fill.
  const askWebsite = useProductBrand().product !== 'toolfare'
  const locale = useLocale()
  const { getToken } = useAuthContext()
  const { request } = useApiRequest()
  const [name, setName] = React.useState('')
  const [website, setWebsite] = React.useState('')
  const [submitting, setSubmitting] = React.useState(false)
  const [statusMessage, setStatusMessage] = React.useState('')
  const openBillingDialog = useBillingDialogStore(s => s.open)

  React.useEffect(() => {
    if (!open) {
      setName('')
      setWebsite('')
      setStatusMessage('')
    }
  }, [open])

  async function attemptCreate(
    slug: string,
    displayName: string,
    brandWebsite: string,
  ): Promise<CreateBrandAttempt> {
    try {
      const brand = await request<CreateBrandPayload>({
        url: '/api/brands',
        method: 'POST',
        body: {
          slug,
          name: displayName,
          ...(brandWebsite ? { brandWebsite } : {}),
        },
      })
      return { ok: true, brand }
    } catch (err) {
      if (err instanceof ApiFetchError) {
        return {
          ok: false,
          status: err.statusCode,
          errorCode: err.code ?? extractErrorCode(err.details),
          suggestedSlug:
            err.statusCode === 409
              ? extractSuggestedBrandSlug(err.details)
              : undefined,
        }
      }
      throw err
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!name.trim()) return
    setSubmitting(true)
    setStatusMessage(t('createDialog.statusCreating'))
    try {
      const initialSlug = slugify(name)
      if (!initialSlug) {
        toast.error(t('errors.brandNameInvalid'))
        return
      }
      const trimmedWebsite = website.trim()
      const brandWebsite = trimmedWebsite
        ? /^https?:\/\//i.test(trimmedWebsite)
          ? trimmedWebsite
          : `https://${trimmedWebsite}`
        : ''
      let createResult = await attemptCreate(
        initialSlug,
        name.trim(),
        brandWebsite,
      )
      if (!createResult.ok && createResult.status === 409) {
        if (createResult.errorCode === 'brand_limit_reached') {
          // Close this dialog first so the upgrade dialog is not stacked
          // behind its focus trap.
          onOpenChange(false)
          track('brand_limit_reached', { surface: 'brand_switcher' })
          openBillingDialog('brand_limit')
          return
        }
        // Retry once with the server's suggested slug.
        const suggested = createResult.suggestedSlug
        if (!suggested) {
          toast.error(t('errors.duplicateBrand'))
          return
        }
        createResult = await attemptCreate(suggested, name.trim(), brandWebsite)
      }
      if (!createResult.ok) {
        toast.error(
          createResult.status === 409
            ? t('errors.duplicateBrandTryAgain')
            : t('errors.createFailed', { status: createResult.status }),
        )
        return
      }
      const createdId = createResult.brand.id
      const createdSlug = createResult.brand.slug
      if (!createdId || !createdSlug) {
        toast.error(t('errors.noSlugReturned'))
        return
      }

      if (brandWebsite) setStatusMessage(t('createDialog.statusAnalyzing'))
      const profileResult = await completeQuickAddBrandProfile({
        brandId: createdId,
        websiteUrl: brandWebsite,
        getToken,
        language: locale,
      })
      if (profileResult.status === 'no_website') {
        toast.message(t('errors.profileIncomplete'))
      } else if (profileResult.status === 'empty') {
        toast.message(t('errors.crawlNoFields'))
      } else if (profileResult.status === 'rate_limited') {
        toast.message(t('errors.crawlRateLimited'))
      } else if (profileResult.status === 'crawl_failed') {
        toast.error(t('errors.crawlFailed'))
      } else if (profileResult.status === 'save_failed') {
        toast.error(
          t('errors.profileUpdateFailed', {
            status: profileResult.httpStatus ?? 0,
          }),
        )
      }

      onCreated(createdSlug)
      onOpenChange(false)
    } finally {
      setSubmitting(false)
      setStatusMessage('')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('createDialog.title')}</DialogTitle>
          <DialogDescription>
            {t.rich('createDialog.description', {
              slugPlaceholder: '<slug>',
              code: chunks => <code>{chunks}</code>,
            })}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="brand-name">{t('createDialog.nameLabel')}</Label>
            <Input
              id="brand-name"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder={t('createDialog.namePlaceholder')}
              autoFocus
              disabled={submitting}
            />
            <p className="text-muted-foreground text-[11px]">
              {t.rich('createDialog.slugHint', {
                slug: slugify(name) || '…',
                code: chunks => <code>{chunks}</code>,
              })}
            </p>
          </div>
          {askWebsite ? (
            <div className="space-y-2">
              <Label htmlFor="brand-website">
                {t('createDialog.websiteLabel')}
              </Label>
              <Input
                id="brand-website"
                type="text"
                inputMode="url"
                autoComplete="url"
                value={website}
                onChange={e => setWebsite(e.target.value)}
                placeholder={t('createDialog.websitePlaceholder')}
                disabled={submitting}
              />
              <p className="text-muted-foreground text-[11px]">
                {t('createDialog.websiteHint')}
              </p>
            </div>
          ) : null}
          <DialogFooter>
            {submitting && statusMessage ? (
              <span className="text-muted-foreground me-auto self-center text-xs">
                {statusMessage}
              </span>
            ) : null}
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              {t('createDialog.cancel')}
            </Button>
            <Button type="submit" disabled={!name.trim() || submitting}>
              {submitting
                ? t('createDialog.creating')
                : t('createDialog.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Switcher
// ---------------------------------------------------------------------------

type CreateMode = 'brand'

function SwitcherLoadingRows({
  label,
  rows = 3,
}: {
  label: string
  rows?: number
}) {
  const widths = ['w-28', 'w-36', 'w-24', 'w-32']

  return (
    <div role="status" aria-label={label} className="space-y-1 p-1">
      <span className="sr-only">{label}</span>
      {Array.from({ length: rows }).map((_, index) => (
        <div
          key={index}
          className="flex h-10 items-center gap-2 rounded-sm px-2 py-2.5"
        >
          <Skeleton className="size-6 shrink-0 rounded-md" />
          <Skeleton className={cn('h-3', widths[index % widths.length])} />
        </div>
      ))}
    </div>
  )
}

export function ClientSwitcher({
  compact = false,
  side,
}: {
  compact?: boolean
  /**
   * Which way the popover opens. Defaults to the natural direction for each
   * trigger shape; pass "top" when the switcher sits in the sidebar footer so
   * the menu grows up over the nav instead of relying on collision flipping.
   */
  side?: 'top' | 'bottom' | 'right'
} = {}) {
  const t = useSwitcherTranslations()
  const router = useRouter()
  const pathname = usePathname()
  // Shared with the rail (same ORG_WORKSPACE_SECTIONS allowlist) so the
  // workspace entry below marks itself active on exactly the routes where
  // the rail is in org scope.
  // Admin-only surface: for a member the workspace entry is not rendered and
  // the routes 404, so "you are in the org workspace" is never true for them
  // — gating the predicate here keeps every derived cue (label, avatar,
  // default highlight) consistent with that in one place.
  // toolfare has no org workspace: its console is the workspace pages only,
  // and the middleware sends `/o/{org}/overview` back to MCP & CLI.
  const { product } = useProductBrand()
  const orgWorkspaceVisible = useOrgAdminGate() && product !== 'toolfare'
  const inOrgWorkspace = orgWorkspaceVisible && isOrgWorkspacePath(pathname)
  const org = useOptionalActiveOrg()
  const brand = useOptionalCurrentBrand()
  // Org list + switch, backed by shared auth context organizations.
  const { orgs, isLoading: isOrgListLoading, switchOrg } = useOrgSwitcher()
  const [open, setOpen] = React.useState(false)
  const [createMode, setCreateMode] = React.useState<CreateMode | null>(null)
  const [pendingBrandSlug, setPendingBrandSlug] = React.useState<string | null>(
    null,
  )
  const [, startBrandSwitchTransition] = React.useTransition()

  const {
    data: brandsData,
    isLoading: brandsLoading,
    mutate: mutateBrands,
  } = useBrandWorkspaces()
  const brands = brandsData ?? EMPTY_BRANDS
  const isBrandListInitialLoading = brandsLoading && brandsData === undefined
  // Cross-brand ambient signal: unread counts for OTHER brands in this org,
  // surfaced as neutral dots so a finished task in a brand the user isn't
  // viewing shows up without touching the current brand's red bell. Called
  // above the early `return null` to satisfy the rules of hooks.
  const { countsByBrand } = useUnreadCountByBrand()
  const activeBrandRow = React.useMemo(() => {
    if (!brand) return null
    return brands.find(b => b.slug === brand.brandSlug) ?? null
  }, [brand, brands])
  // Switcher list: active brand pinned at the top, everything else
  // alphabetically. Rendered as two CommandGroups so cmdk draws a
  // separator between them — mirrors the Linear / Notion pattern
  // (current workspace anchored, then long-tail). Both groups still
  // filter independently on search, so the active row drops out if
  // the user's query doesn't match it.
  const sortedBrands = React.useMemo(
    () => [...brands].sort((a, b) => a.name.localeCompare(b.name)),
    [brands],
  )
  const otherBrands = React.useMemo(
    () => sortedBrands.filter(b => b.id !== activeBrandRow?.id),
    [sortedBrands, activeBrandRow],
  )
  // Entry-button dot: light only when a brand OTHER than the active one has
  // unread. Keyed on `otherBrands` (never `Object.keys(countsByBrand)`) so the
  // active brand's own unread — already shown by the red bell — can't
  // double-signal here. Gated on a resolved `activeBrandRow` so a stale brand
  // slug (active row not yet in the SWR list) can't briefly leak the current
  // brand's count into the dot.
  const hasOtherBrandUnread =
    activeBrandRow != null &&
    otherBrands.some(b => (countsByBrand[b.id] ?? 0) > 0)
  const isBrandSwitching = pendingBrandSlug !== null
  const isBrandBusy = isBrandSwitching || isBrandListInitialLoading

  React.useEffect(() => {
    if (!pendingBrandSlug) return
    if (brand?.brandSlug === pendingBrandSlug) {
      setPendingBrandSlug(null)
      return
    }

    const timeout = window.setTimeout(() => {
      setPendingBrandSlug(null)
    }, 15_000)

    return () => window.clearTimeout(timeout)
  }, [brand?.brandSlug, pendingBrandSlug])

  if (!org) {
    // Defensive: provider returned null (unauthenticated edge case or
    // backend resolver failure). The switcher only renders inside the
    // authenticated chrome, so this is rare. Render nothing to avoid a
    // crash; the chrome stays usable.
    return null
  }
  const activeOrgRow = orgs.find(o => o.id === org.id)
  // Split orgs into [active, ...others sorted by name] so the switcher pins
  // the current org at the top (both providers return an unsorted list).
  const otherOrgs = orgs
    .filter(o => o.id !== org.id)
    .sort((a, b) => a.name.localeCompare(b.name))

  async function handleSelectOrg(orgId: string) {
    setOpen(false)
    // Clicking the already-active org (the pinned-at-top current row) is an
    // entry, not a switch: soft-navigate to the org workspace overview. The
    // scope does not change, so the hard-reload switch path below would be
    // a wasted round-trip. A member has no org workspace to enter, so their
    // "entry" is the brand resolver — never the overview, which 404s.
    if (orgId === org.id) {
      router.push(buildOrgUrl(org.slug, orgWorkspaceVisible ? 'overview' : 'b'))
      return
    }
    const target = orgs.find(o => o.id === orgId)
    if (!target?.slug) {
      toast.error(t('errors.switchOrgFailed'))
      return
    }
    try {
      // Hard navigation (handled inside switchOrg): a soft router.push only
      // re-pulls RSC data, but client caches (SWR brands list, the org store,
      // route-level state) can keep stale fragments from the previous org. A
      // full reload guarantees every layer re-resolves under the new scope.
      // Destination `/o/{slug}` redirects to the first brand (or `/b/new`).
      await switchOrg(target)
    } catch (err) {
      console.error('org switch failed', err)
      toast.error(t('errors.switchOrgFailed'))
      return
    }
  }

  function handleSelectBrand(row: BrandWorkspace) {
    setOpen(false)
    if (row.slug === brand?.brandSlug) return
    setPendingBrandSlug(row.slug)
    startBrandSwitchTransition(() => {
      // Stay on the current page under the new brand; brand-owned resource
      // detail pages (a conversation, a site) fall back to their list page.
      router.push(buildBrandSwitchUrl(pathname, org.slug, row.slug))
    })
  }

  function handleBrandCreated(slug: string) {
    void mutateBrands()
    // URL-driven landing: CurrentBrandProvider in the new brand layout picks the
    // new brand up via the slug segment; no cookie write is needed.
    router.push(buildBrandUrl(org.slug, slug, 'chat/new'))
  }

  const orgDisplayName = activeOrgRow?.name ?? org.name
  // Inside the org workspace there is deliberately no active brand, so the
  // trigger names the ORG as the current scope. Without this it falls through
  // to "Choose brand", which makes a chosen scope look like a missing
  // selection — the switcher would be the only place in the UI that cannot
  // say where you are.
  const brandDisplayName = inOrgWorkspace
    ? t('organizationWorkspace')
    : (brand?.name ?? t('chooseBrand'))

  const resolvedSide = side ?? (compact ? 'right' : 'bottom')

  const switcherInner = (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <Tooltip>
          <PopoverTrigger asChild>
            <TooltipTrigger asChild>
              <button
                type="button"
                // In the org workspace there is no brand by design — it is
                // still a switcher, not a "create brand" affordance.
                aria-label={
                  brand || inOrgWorkspace ? t('switchBrand') : t('createBrand')
                }
                aria-busy={isBrandBusy}
                data-state={open ? 'open' : 'closed'}
                className={cn(
                  'hover:bg-sidebar-accent data-[state=open]:bg-sidebar-accent transition-colors',
                  compact
                    ? 'flex size-9 items-center justify-center rounded-md'
                    : 'flex h-9 min-w-0 flex-1 items-center gap-1.5 rounded-md px-1.5 py-1',
                )}
              >
                <div className="relative shrink-0">
                  {compact && isBrandBusy ? (
                    <Loader2Icon className="text-muted-foreground size-5 animate-spin" />
                  ) : (
                    <BrandAvatar
                      subject={
                        inOrgWorkspace
                          ? {
                              name: orgDisplayName,
                              logoSrc: activeOrgRow?.imageUrl ?? org.imageUrl,
                            }
                          : {
                              name: brand?.name ?? '?',
                              // Prefer the SWR row (picks up uploads without a
                              // hard reload) but fall back to scope so the icon
                              // renders from SSR — otherwise the avatar flashes
                              // initials before /api/brands/summary resolves on
                              // the client.
                              logoSrc:
                                activeBrandRow?.iconUrl ??
                                brand?.iconUrl ??
                                null,
                            }
                      }
                      size={compact ? 24 : 20}
                    />
                  )}
                  {hasOtherBrandUnread && !(compact && isBrandBusy) && (
                    // Neutral (not red) ambient dot: another brand has unread.
                    // Low-key by design — discovered on open, not a demand for
                    // attention like the current brand's bell.
                    <span
                      aria-label={t('otherBrandUnread')}
                      className="bg-muted-foreground/60 ring-sidebar absolute -top-0.5 -end-0.5 size-2 rounded-full ring-2"
                    />
                  )}
                </div>
                {compact ? null : (
                  <>
                    <span className="text-muted-foreground min-w-0 flex-1 truncate text-start text-sm">
                      {brandDisplayName}
                    </span>
                    {isBrandBusy ? (
                      <Loader2Icon className="text-sidebar-foreground/40 size-3.5 shrink-0 animate-spin" />
                    ) : (
                      <ChevronsUpDownIcon className="text-sidebar-foreground/40 size-3.5 shrink-0" />
                    )}
                  </>
                )}
              </button>
            </TooltipTrigger>
          </PopoverTrigger>
          <TooltipContent side={resolvedSide} sideOffset={6}>
            <span className="font-medium">{brandDisplayName}</span>
            <span className="text-muted-foreground"> · {orgDisplayName}</span>
          </TooltipContent>
        </Tooltip>
        <PopoverContent
          side={resolvedSide}
          align="start"
          sideOffset={8}
          className="w-72 p-0 shadow-lg"
        >
          {/* One tree menu: active org pinned with its brands nested, then the
              other orgs as switch rows. The initial cursor lands on wherever
              you already are — the workspace row inside the org workspace,
              the active brand otherwise — so the highlight reinforces the
              current location instead of parking on the first row and reading
              like a selection you did not make. */}
          <Command
            defaultValue={
              inOrgWorkspace
                ? ORG_WORKSPACE_ITEM_VALUE
                : activeBrandRow
                  ? `${orgDisplayName} ${activeBrandRow.name} ${activeBrandRow.id}`
                  : undefined
            }
          >
            <CommandInput placeholder={t('searchPlaceholder')} />
            <CommandList className="max-h-[60vh]">
              <CommandEmpty>{t('noResults')}</CommandEmpty>

              {/* Active org + its brands (the expanded branch of the tree) */}
              <CommandGroup>
                <CommandItem
                  // Append id so cmdk treats same-named orgs as distinct rows
                  // (otherwise the active checkmark + keyboard highlight collide).
                  value={`org ${orgDisplayName} ${org.id}`}
                  // Entry to the org workspace: same-org select soft-navigates
                  // to `/o/{slug}/overview` (handleSelectOrg's same-org branch).
                  onSelect={() => handleSelectOrg(org.id)}
                  className="flex items-center gap-2"
                >
                  <BrandAvatar
                    subject={{
                      name: orgDisplayName,
                      logoSrc: activeOrgRow?.imageUrl ?? org.imageUrl,
                    }}
                    size={22}
                  />
                  <span className="min-w-0 flex-1 truncate text-sm font-medium">
                    {orgDisplayName}
                  </span>
                  {/* No checkmark here: the tree already says this is the
                      active org (its brands are expanded; every other org sits
                      under "Switch organization"). A second checkmark would
                      compete with the one marking where you actually are. */}
                  {/* Affordance that this row navigates rather than just
                      marking the active org. Keyed off cmdk's data-selected
                      (set on BOTH pointer hover and keyboard highlight) so
                      keyboard users get the same hint. `group/command-item`
                      is declared by CommandItem itself. */}
                  <ChevronRightIcon
                    aria-hidden
                    className="text-muted-foreground size-4 shrink-0 opacity-0 transition-opacity group-data-[selected=true]/command-item:opacity-100 rtl:-scale-x-100"
                  />
                </CommandItem>

                {/* Redundant, self-describing entry to the org workspace —
                    the org row above navigates too, but reads as a status
                    marker on its own. Indented to brand level: it is a
                    surface inside this org, not another org.

                    Admin-only, matching the rail: a member must not be
                    offered a destination that 404s for them. */}
                {orgWorkspaceVisible ? (
                  <CommandItem
                    value={ORG_WORKSPACE_ITEM_VALUE}
                    onSelect={() => {
                      setOpen(false)
                      router.push(buildOrgUrl(org.slug, 'overview'))
                    }}
                    className="ms-3 flex items-center gap-2"
                  >
                    {/* Being here is carried by the icon tile inverting to
                      solid, not by a checkmark: the palette is a neutral grey
                      ramp (--primary is near-black), so another tint of grey
                      would not read. Foreground/background inversion survives
                      both themes. */}
                    <span
                      className={cn(
                        'flex size-[22px] shrink-0 items-center justify-center rounded-md border transition-colors',
                        inOrgWorkspace
                          ? 'bg-foreground text-background border-transparent'
                          : 'border-muted-foreground/30 text-muted-foreground',
                      )}
                    >
                      {/* Same glyph the rail uses for the org menu
                        (`ORG_RAIL_ICONS.overview`) — one destination should
                        not have two identities.

                        The `!` is load-bearing: CommandItem carries
                        `data-selected:**:[svg]:text-foreground`, which targets
                        descendant svgs directly and so outranks the inherited
                        `text-background` from the tile. Without it, hovering
                        the active row paints a black glyph on a black tile. */}
                      <Building2Icon
                        className={cn(
                          'size-3.5',
                          inOrgWorkspace && 'text-background!',
                        )}
                      />
                    </span>
                    <span
                      className={cn(
                        'min-w-0 flex-1 truncate text-sm',
                        inOrgWorkspace && 'font-medium',
                      )}
                    >
                      {t('organizationWorkspace')}
                    </span>
                    {/* Same trailing checkmark a brand row gets, so "where you
                      are" reads identically for both kinds of destination —
                      the solid tile above is an extra cue, not a substitute. */}
                    {inOrgWorkspace ? (
                      <CheckIcon className="text-primary size-4 shrink-0" />
                    ) : null}
                  </CommandItem>
                ) : null}

                {isBrandListInitialLoading ? (
                  <SwitcherLoadingRows label={t('loadingBrands')} rows={3} />
                ) : (
                  sortedBrands.map(b => {
                    const isActive = b.id === activeBrandRow?.id
                    return (
                      <CommandItem
                        key={b.id}
                        value={`${orgDisplayName} ${b.name} ${b.id}`}
                        onSelect={() => handleSelectBrand(b)}
                        disabled={isBrandSwitching}
                        className="ms-3 flex items-center gap-2"
                      >
                        <BrandAvatar
                          subject={{
                            name: b.name,
                            logoSrc:
                              b.iconUrl ??
                              (isActive ? brand?.iconUrl : null) ??
                              null,
                          }}
                          size={22}
                          loading="lazy"
                        />
                        {/* Same "you are here" weight as the workspace row —
                            a brand avatar cannot invert like the icon tile, so
                            the active brand carries it in the type. */}
                        <span
                          className={cn(
                            'min-w-0 flex-1 truncate text-sm',
                            isActive && 'font-medium',
                          )}
                        >
                          {b.name}
                        </span>
                        {pendingBrandSlug === b.slug ? (
                          <Loader2Icon className="text-muted-foreground size-4 shrink-0 animate-spin" />
                        ) : isActive ? (
                          <CheckIcon className="text-primary size-4 shrink-0" />
                        ) : (countsByBrand[b.id] ?? 0) > 0 ? (
                          // Neutral ambient marker (not the red bell style):
                          // this brand has unread while the user is elsewhere.
                          <span
                            aria-label={t('unreadCount', {
                              count: countsByBrand[b.id],
                            })}
                            className="bg-muted text-muted-foreground flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full px-1 font-mono text-[10px] leading-none font-medium tracking-tighter"
                          >
                            {countsByBrand[b.id] > 99
                              ? '99+'
                              : countsByBrand[b.id]}
                          </span>
                        ) : null}
                      </CommandItem>
                    )
                  })
                )}

                <CommandItem
                  value="__create-brand"
                  onSelect={() => {
                    setOpen(false)
                    setCreateMode('brand')
                  }}
                  className="ms-3 flex items-center gap-2 text-sm"
                >
                  <span className="border-muted-foreground/40 text-muted-foreground flex size-[22px] shrink-0 items-center justify-center rounded-md border border-dashed">
                    <PlusIcon className="size-3.5" />
                  </span>
                  <span className="text-muted-foreground">
                    {t('createBrandInOrg', { org: orgDisplayName })}
                  </span>
                </CommandItem>
              </CommandGroup>

              {/* Other orgs — bare switch rows (their brands load after the
                  hard navigation lands under the new scope). */}
              {(isOrgListLoading || otherOrgs.length > 0) && (
                <CommandSeparator className="bg-sidebar-foreground/10 mx-5 my-1" />
              )}
              {isOrgListLoading ? (
                <CommandGroup>
                  <SwitcherLoadingRows
                    label={t('loadingOrganizations')}
                    rows={2}
                  />
                </CommandGroup>
              ) : otherOrgs.length > 0 ? (
                <CommandGroup heading={t('switchOrganization')}>
                  {otherOrgs.map(o => (
                    <CommandItem
                      key={o.id}
                      value={`org ${o.name} ${o.id}`}
                      onSelect={() => handleSelectOrg(o.id)}
                      className="flex items-center gap-2"
                    >
                      <BrandAvatar
                        subject={{ name: o.name, logoSrc: o.imageUrl ?? null }}
                        size={22}
                        loading="lazy"
                      />
                      <span className="min-w-0 flex-1 truncate text-sm">
                        {o.name}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              ) : null}

              <CommandSeparator className="bg-sidebar-foreground/10 mx-5 my-1" />
              <CommandGroup>
                <CommandItem
                  value="__create-org"
                  onSelect={() => {
                    setOpen(false)
                    router.push('/onboarding/new-org')
                  }}
                  className="flex items-center gap-2 text-sm"
                >
                  <span className="border-muted-foreground/40 text-muted-foreground flex size-[22px] shrink-0 items-center justify-center rounded-md border border-dashed">
                    <PlusIcon className="size-3.5" />
                  </span>
                  <span className="text-muted-foreground">
                    {t('createOrganization')}
                  </span>
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <CreateBrandDialog
        open={createMode === 'brand'}
        onOpenChange={open => {
          if (!open) setCreateMode(null)
        }}
        onCreated={handleBrandCreated}
      />
    </>
  )

  if (compact) {
    return switcherInner
  }

  return (
    <SidebarMenu>
      {/* No plan chip here. It shared this row with the org switcher and the
          account avatar, so its width came straight out of the org name's —
          and plan names are not short ("Enterprise", "Creator"), which made
          the truncation worst exactly for the orgs with the most to say. The
          plan is a low-frequency fact with its own rail entry (Billing) and
          its own page; it does not need permanent chrome. Running out of
          credits, the one state that IS urgent, is surfaced where it blocks
          you — disabled send in chat-input, the 402 dialog — not here. */}
      <SidebarMenuItem className="flex min-w-0 items-center gap-1">
        {switcherInner}
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
